import { createContext, useContext, useEffect, useRef, useState } from "react";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import PreStreamAd, { shouldShowPreStream, markPreStreamShown } from "../components/PreStreamAd";

const PlayerContext = createContext(null);

export function PlayerProvider({ children }) {
    const audioRef = useRef(null);
    const [current, setCurrent] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolume] = useState(0.8);
    const [streamMode, setStreamMode] = useState("preview");
    // Queue: list of tracks set by the originating view (catalog, playlist, etc.).
    // `queueIndex` points to the currently playing track in that list.
    const [queue, setQueue] = useState([]);
    const [queueIndex, setQueueIndex] = useState(-1);
    // Pre-stream ad state
    const [prestream, setPrestream] = useState(null); // { zoneId, duration, pendingTrack }
    const [expanded, setExpanded] = useState(false);   // lecteur plein écran
    const monetagCfgRef = useRef(null); // cache /public/monetag-status

    useEffect(() => {
        // Charge la config Monetag une seule fois pour connaître prestream zone + duration.
        api.get("/public/monetag-status").then(({ data }) => {
            monetagCfgRef.current = data;
        }).catch(() => { monetagCfgRef.current = null; });
    }, []);

    useEffect(() => {
        const a = audioRef.current;
        if (!a) return;
        // Track whether we've already logged the "validated stream" (>= 30s of playback)
        // for the currently loaded track — only one log per play session.
        let streamLogged = false;
        const startedAt = Date.now();
        const onTime = () => {
            setProgress(a.currentTime);
            // Log validated stream once the 30-second threshold is reached.
            if (!streamLogged && a.currentTime >= 30 && current?.id) {
                streamLogged = true;
                api.post("/streams/log", {
                    track_id: current.id,
                    duration_sec: a.currentTime,
                }).catch(() => {});
            }
        };
        const onDur = () => setDuration(a.duration || 0);
        const onEnd = () => {
            // Auto-advance to the next track in the queue if any.
            setPlaying(false);
            playNextRef.current && playNextRef.current(true);
        };
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onDur);
        a.addEventListener("ended", onEnd);
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onDur);
            a.removeEventListener("ended", onEnd);
            // Silence lint on unused startedAt: kept for future analytics.
            void startedAt;
        };
    }, [current]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.volume = volume;
    }, [volume]);

    const resolveUrl = (track) => {
        const token = tokenStore.get();
        if (token && track?.id && track?.audio_url) {
            return {
                url: `${BACKEND_URL}/api/tracks/${track.id}/stream?t=${encodeURIComponent(token)}`,
                mode: "full",
            };
        }
        return { url: track?.preview_url || track?.audio_url, mode: "preview" };
    };

    // Warm up the next track of the queue while the current one plays: a tiny
    // Range request makes the server fetch it into its cache, so « suivant »
    // starts almost instantly. Costs ~2 bytes of mobile data.
    useEffect(() => {
        if (!playing || queueIndex < 0) return undefined;
        const next = queue[queueIndex + 1];
        if (!next) return undefined;
        const timer = setTimeout(() => {
            const { url } = resolveUrl(next);
            if (url && BACKEND_URL && url.startsWith(BACKEND_URL)) {
                fetch(url, { headers: { Range: "bytes=0-1" }, credentials: "include" }).catch(() => {});
            }
        }, 4000);
        return () => clearTimeout(timer);
    }, [playing, queueIndex, queue]); // eslint-disable-line react-hooks/exhaustive-deps

    const _startTrack = (track) => {
        const { url, mode } = resolveUrl(track);
        if (!url) return false;
        setCurrent(track);
        setStreamMode(mode);
        setPlaying(true);
        if (track?.id) {
            api.post(`/tracks/${track.id}/play`).catch(() => {});
        }
        // Start immediately (no setTimeout) for snappier feedback on mobile and PC.
        const a = audioRef.current;
        if (a) {
            a.src = url;
            const fallback = mode === "full" ? (track?.preview_url || null) : null;
            let errored = false;
            const onErr = () => {
                if (errored) return;
                errored = true;
                if (fallback && audioRef.current) {
                    audioRef.current.src = fallback;
                    setStreamMode("preview");
                    audioRef.current.play().catch(() => setPlaying(false));
                } else {
                    setPlaying(false);
                    // Morceau illisible → on passe automatiquement au suivant.
                    playNextRef.current && playNextRef.current(true);
                }
            };
            a.onerror = onErr;
            // load() forces the browser to start fetching immediately
            try { a.load(); } catch { /* ignore */ }
            a.play().catch(onErr);
        }
        return true;
    };

    const _actuallyPlay = (track) => {
        if (current && current.id === track.id && audioRef.current) {
            audioRef.current.play().catch(() => setPlaying(false));
            setPlaying(true);
            return;
        }
        _startTrack(track);
    };

    const play = (track, opts) => {
        // opts.queue (Array) sets the playback queue. Index defaults to the
        // position of `track` within the queue (or -1 if absent).
        if (opts && Array.isArray(opts.queue) && opts.queue.length > 0) {
            const idx = opts.queue.findIndex((t) => t?.id === track?.id);
            setQueue(opts.queue);
            setQueueIndex(idx);
        } else {
            // Single-track play: keep just this track in the queue.
            setQueue([track]);
            setQueueIndex(0);
        }
        // Pre-stream ad interception : ne se déclenche que sur user action
        // (pas sur auto-advance queue). opts.skipPreStream = bypass.
        const cfg = monetagCfgRef.current;
        const preZone = cfg?.enabled && cfg?.placements?.prestream;
        const preDur = cfg?.prestream_duration ?? 5;
        if (preZone && preDur > 0 && !opts?.skipPreStream && shouldShowPreStream()) {
            markPreStreamShown();
            setPrestream({ zoneId: preZone, duration: preDur, pendingTrack: track });
            return;
        }
        _actuallyPlay(track);
    };

    const toggle = () => {
        if (!audioRef.current || !current) return;
        if (playing) {
            audioRef.current.pause();
            setPlaying(false);
        } else {
            audioRef.current.play().catch(() => setPlaying(false));
            setPlaying(true);
        }
    };

    const seek = (t) => {
        if (audioRef.current) {
            audioRef.current.currentTime = t;
            setProgress(t);
        }
    };

    const stop = () => {
        if (audioRef.current) audioRef.current.pause();
        setPlaying(false);
        setCurrent(null);
        setQueue([]);
        setQueueIndex(-1);
        setExpanded(false);
    };

    const playNext = (auto = false) => {
        if (queue.length === 0) return;
        // Saute les morceaux sans source audio jouable.
        for (let i = queueIndex + 1; i < queue.length; i++) {
            const t = queue[i];
            if (t?.preview_url || t?.audio_url) {
                setQueueIndex(i);
                _startTrack(t);
                return;
            }
        }
        if (auto) setPlaying(false);
    };

    const playPrev = () => {
        if (queue.length === 0) return;
        // If we are past the first 3 seconds, restart current track instead of jumping back.
        if (progress > 3 && audioRef.current) {
            audioRef.current.currentTime = 0;
            setProgress(0);
            return;
        }
        for (let i = queueIndex - 1; i >= 0; i--) {
            const t = queue[i];
            if (t?.preview_url || t?.audio_url) {
                setQueueIndex(i);
                _startTrack(t);
                return;
            }
        }
        if (audioRef.current) audioRef.current.currentTime = 0;
    };

    // Keep a ref to playNext so the audio "ended" handler can call it without
    // having a stale closure on queue/queueIndex.
    const playNextRef = useRef();
    playNextRef.current = playNext;

    const hasNext = queue.length > 0 && queueIndex >= 0 && queueIndex < queue.length - 1;
    const hasPrev = queue.length > 0 && queueIndex > 0;

    // Lecture d'un morceau précis de la file (liste « À suivre » du lecteur plein écran)
    const playAt = (i) => {
        const t = queue[i];
        if (!t || !(t.preview_url || t.audio_url)) return;
        setQueueIndex(i);
        _startTrack(t);
    };

    // ---- Écran de verrouillage / notification (Media Session API)
    // Le téléphone affiche un grand lecteur avec la pochette, le titre et les boutons
    // précédent / lecture / suivant, même écran éteint ou application en arrière-plan.
    const ms = typeof navigator !== "undefined" && "mediaSession" in navigator ? navigator.mediaSession : null;
    const actionsRef = useRef({});
    actionsRef.current = { toggle, playNext, playPrev, seek, progress };
    useEffect(() => {
        if (!ms) return;
        if (!current) { ms.metadata = null; ms.playbackState = "none"; return; }
        const art = current.cover_url;
        try {
            // eslint-disable-next-line no-undef
            ms.metadata = new MediaMetadata({
                title: current.title || "MBOAZICK",
                artist: current.artist_name || "MBOAZICK",
                album: "MBOAZICK",
                artwork: art ? ["96x96", "256x256", "512x512"].map((sizes) => ({ src: art, sizes })) : [{ src: "/icons/icon-512.png", sizes: "512x512", type: "image/png" }],
            });
        } catch { /* navigateur sans MediaMetadata */ }
    }, [current, ms]);
    useEffect(() => { if (ms && current) ms.playbackState = playing ? "playing" : "paused"; }, [playing, current, ms]);
    useEffect(() => {
        if (!ms) return undefined;
        const set = (action, fn) => { try { ms.setActionHandler(action, fn); } catch { /* action non prise en charge */ } };
        set("play", () => { if (!actionsRef.current) return; audioRef.current?.play().then(() => setPlaying(true)).catch(() => {}); });
        set("pause", () => { audioRef.current?.pause(); setPlaying(false); });
        set("previoustrack", () => actionsRef.current.playPrev());
        set("nexttrack", () => actionsRef.current.playNext(false));
        set("seekto", (d) => { if (d && typeof d.seekTime === "number") actionsRef.current.seek(d.seekTime); });
        set("seekbackward", (d) => actionsRef.current.seek(Math.max(0, (audioRef.current?.currentTime || 0) - (d?.seekOffset || 10))));
        set("seekforward", (d) => actionsRef.current.seek((audioRef.current?.currentTime || 0) + (d?.seekOffset || 10)));
        return () => ["play", "pause", "previoustrack", "nexttrack", "seekto", "seekbackward", "seekforward"].forEach((a) => set(a, null));
    }, [ms]);
    // Barre de progression sur l'écran de verrouillage (mise à jour toutes les ~5 s)
    const lastPos = useRef(0);
    useEffect(() => {
        if (!ms || !ms.setPositionState || !duration || !isFinite(duration)) return;
        if (Math.abs(progress - lastPos.current) < 5 && progress > 1) return;
        lastPos.current = progress;
        try { ms.setPositionState({ duration, position: Math.min(progress, duration), playbackRate: 1 }); } catch { /* ignore */ }
    }, [progress, duration, ms]);

    return (
        <PlayerContext.Provider
            value={{
                current, playing, progress, duration, volume, streamMode,
                play, toggle, seek, stop, setVolume,
                playNext, playPrev, hasNext, hasPrev,
                queue, queueIndex, playAt, expanded, setExpanded,
            }}
        >
            {children}
            <audio ref={audioRef} preload="metadata" data-testid="global-audio-element" />
            <PreStreamAd
                open={!!prestream}
                zoneId={prestream?.zoneId}
                duration={prestream?.duration || 5}
                onComplete={() => {
                    const t = prestream?.pendingTrack;
                    setPrestream(null);
                    if (t) _actuallyPlay(t);
                }}
                onClose={() => {
                    const t = prestream?.pendingTrack;
                    setPrestream(null);
                    if (t) _actuallyPlay(t);
                }}
            />
        </PlayerContext.Provider>
    );
}

export const usePlayer = () => useContext(PlayerContext);
